import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const ApplicationModal = ({ isOpen, onClose, posting, type = 'job' }) => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    coverLetter: '',
    resumeFile: null,
    shareProfile: true
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload a PDF or Word document');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        resumeFile: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('Please log in to apply');
      return;
    }

    if (!formData.shareProfile) {
      alert('You must consent to share your profile information');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Upload resume file
      let resumeUrl = null;
      if (formData.resumeFile) {
        // Generate upload URL
        const uploadResponse = await fetch('https://us-central1-the-apn.cloudfunctions.net/generateUploadUrl', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({
            fileName: formData.resumeFile.name,
            fileType: formData.resumeFile.type,
            uploadType: 'document'
          })
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to generate upload URL');
        }

        const { uploadUrl, filePath } = await uploadResponse.json();

        // Upload file directly to storage
        const fileUploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': formData.resumeFile.type
          },
          body: formData.resumeFile
        });

        if (!fileUploadResponse.ok) {
          throw new Error('Failed to upload resume');
        }

        // Confirm upload and get public URL
        const confirmResponse = await fetch('https://us-central1-the-apn.cloudfunctions.net/confirmFileUpload', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({
            filePath,
            uploadType: 'document',
            metadata: {
              originalName: formData.resumeFile.name,
              postingId: posting.id,
              postingType: type
            }
          })
        });

        if (!confirmResponse.ok) {
          throw new Error('Failed to confirm file upload');
        }

        const { fileUrl } = await confirmResponse.json();
        resumeUrl = fileUrl;
      }

      // Step 2: Submit application
      const applicationData = {
        postingId: posting.id,
        postingType: type,
        postingTitle: posting.title,
        applicantId: currentUser.uid,
        applicantName: `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim(),
        applicantEmail: userProfile?.email || currentUser.email,
        coverLetter: formData.coverLetter,
        resumeUrl,
        resumeFileName: formData.resumeFile?.name,
        shareProfile: formData.shareProfile,
        appliedAt: new Date().toISOString(),
        status: 'submitted'
      };

      // Submit application via API
      const applicationResponse = await fetch('https://us-central1-the-apn.cloudfunctions.net/submitApplication', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(applicationData)
      });

      if (!applicationResponse.ok) {
        throw new Error('Failed to submit application');
      }

      // Success!
      alert('Application submitted successfully!');
      onClose();
      
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Apply for {type === 'job' ? 'Job' : 'Project'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Posting Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-lg">{posting.title}</h3>
            <p className="text-gray-600">
              {posting.company || posting.client} • {posting.location}
            </p>
            {posting.salary && <p className="text-green-600 font-medium">{posting.salary}</p>}
            {posting.budget && <p className="text-green-600 font-medium">{posting.budget}</p>}
          </div>

          {/* Application Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Pre-filled Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim()}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userProfile?.email || currentUser?.email || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cover Letter (Optional)
              </label>
              <textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell the employer why you're interested in this position and what makes you a great fit..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Attachment Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachment <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Upload a document (PDF, Word, or TXT, max 10MB)
              </p>
              {formData.resumeFile && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ {formData.resumeFile.name} selected
                </p>
              )}
            </div>

            {/* Consent */}
            <div className="flex items-start">
              <input
                type="checkbox"
                name="shareProfile"
                checked={formData.shareProfile}
                onChange={handleInputChange}
                required
                className="mt-1 mr-3"
              />
              <label className="text-sm text-gray-700">
                I consent to share my profile information and resume with the {type === 'job' ? 'employer' : 'project creator'} for this application.
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;
